import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData
} from '@tanstack/react-query';
import { packingApi } from './packing.api';
import type { BoxesListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const boxKeys = {
  all: ['boxes'] as const,
  lists: () => [...boxKeys.all, 'list'] as const,
  list: (filters: BoxesListParams) => [...boxKeys.lists(), filters] as const,
  infiniteLists: () => [...boxKeys.all, 'infinite'] as const,
  infiniteList: (filters: BoxesListParams) =>
    [...boxKeys.infiniteLists(), filters] as const,
  details: () => [...boxKeys.all, 'detail'] as const,
  detail: (id: number) => [...boxKeys.details(), id] as const,
  metrics: () => [...boxKeys.all, 'metrics'] as const
};

export const packingQueries = {
  list: (filters: BoxesListParams = {}) =>
    queryOptions({
      queryKey: boxKeys.list(filters),
      queryFn: () => packingApi.getAll(filters),
      placeholderData: keepPreviousData
    }),

  /**
   * Page-cursor infinite variant for scroll-to-load surfaces (e.g. the
   * shipping box-selection panel). `filters` must not include `page` —
   * the cursor owns it.
   */
  infiniteList: (filters: BoxesListParams = {}) =>
    infiniteQueryOptions({
      queryKey: boxKeys.infiniteList(filters),
      queryFn: ({ pageParam }) =>
        packingApi.getAll({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasNext ? lastPage.pagination.page + 1 : undefined,
      placeholderData: keepPreviousData
    }),

  detail: (id: number) =>
    queryOptions({
      queryKey: boxKeys.detail(id),
      queryFn: () => packingApi.getById(id),
      enabled: id > 0
    }),

  metrics: () =>
    queryOptions({
      queryKey: boxKeys.metrics(),
      queryFn: () => packingApi.metrics(),
      staleTime: STALE_TIME.metrics
    })
};
